import {
  Component,
  OnInit,
  AfterViewInit,
  Injectable,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  inject,
  effect,
  input,
  Input,
  Output,
  EventEmitter,
  LOCALE_ID,
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs/internal/Subject";
import {
  DatabaseCacheService,
} from "src/app/data/cache/database-cache.service";
import { CreateGost2Service } from "src/app/data/record/create-gost2.service";
import { GostUtils } from "./gost-utils";
import { Location } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from "@angular/material/core";
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from "@angular/material-moment-adapter";
import { finalize } from "rxjs/operators";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GOST_I18N, getRecommendationsArray, getWeekdaysArray } from "./gost.i18n";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";

export interface PeriodicElement {
  type: string;
  score: string;
  value: string;
}

type GostOverviewWstAttemptRow = {
  amount: number | null;
  firstStatus: GostOverviewWstRoundStatus;
  secondStatus: GostOverviewWstRoundStatus | null;
  hasSecond: boolean;
  thicknessStatus: GostOverviewWstRoundStatus | null;
};

type GostOverviewWstRoundStatus = {
  result: string;
  measured: boolean;
  swallowPresent: boolean;
  coughPresent: boolean;
  abnormalCount: number;
};

type GostOverviewRiskItem = {
  section: "GC" | "OC";
  itemLabel: string;
  optionLabel: string;
  severity: "bad" | "acceptable";
};

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const MY_FORMATS = {
  parse: {
    dateInput: "YYYY/MM/DD",
  },
  display: {
    dateInput: "YYYY/MM/DD",
    monthLabel: "MMMM",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

// to customize days name in calendar
@Injectable()
class CustomDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: "long" | "short" | "narrow") {
    return getWeekdaysArray();
  }
  override getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }
  getFirstDayOfWeek(): number {
    // week starts on sunday
    return 0;
  }
}

@Component({
  imports: [...SHARED_IMPORTS],
  selector: "gost-digest-card",
  templateUrl: "./gost-digest.component.html",
  styleUrls: ["./recording.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GostUtils,
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeId: string) =>
        (localeId || "").toLowerCase().startsWith("en") ? "en-US" : "ja-JP",
      deps: [LOCALE_ID],
    },
  ],
  host: {
    "(window:scroll)": "onWindowScroll()",
  },
})
export class GostDigestComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly common = inject(DatabaseCacheService);
  private readonly createGost2Service = inject(CreateGost2Service);
  readonly gostUtils = inject(GostUtils);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  protected readonly i18n = GOST_I18N;

  // 入力シグナル（外部から渡される）
  readonly recordDataInput = input<any>(null, { alias: 'recordData' });
  readonly recordDataUpdated = input<Subject<any>>();

  @Input() loading = false;
  @Input() canNavigateOlder = false;
  @Input() canNavigateNewer = false;
  @Output() readonly navigateOlder = new EventEmitter<void>();
  @Output() readonly navigateNewer = new EventEmitter<void>();

  // ローカルの書き込み可能シグナル（内部での更新に使用）
  readonly recordData = signal<any>(null);

  dataSource: PeriodicElement[];

  readonly panelOpenState = signal(false);

  asOfDate: string | null = null;

  // グリッドで表示するデータを保持する配列
  gridItems: Array<{
    title: string;
    contentFirst: string;
    contentSecond: string;
  }> = [];

  // ランク順にソートされたPrimarilySelectedFoodsのデータを保持する配列
  sortedSelectedFoods: any[] = [];

  // Overview detail-grid data
  overviewWstAttemptRows: GostOverviewWstAttemptRow[] = [];
  overviewRiskItems: GostOverviewRiskItem[] = [];
  hasThicknessData = false;

  recommendations = getRecommendationsArray();
  FILSSCALE: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  SCALE: number[] = [1, 2, 3, 4, 5, 6, 7, 8];
  NA = GOST_I18N.common.noMatch;

  sliderValue = {
    FILS: 1,
    SC: 1,
    GOST: 1,
    DSS: 1,
  };

  readonly displayScoreValue = (value: number): string => value.toString();
  readonly showDssScore = false;

  isLoadingGOSTDataCard = signal(false);
  readonly isSavingManual = signal(false);
  readonly isCopyingPrevious = signal(false);

  /// 入力モードで保存・ ボタンを固定するかどうかのフラグ
  isFixed = false;

  moveBackUrl: string;
  private initialized = false;
  private inputBaselineSnapshot: string | null = null;
  private inputBaselineRecord: any | null = null;
  private inputBaselineCreatedAt: string | null = null;

  constructor() {
    // 入力シグナルの変化をローカルシグナルに同期
    effect(() => {
      const data = this.recordDataInput();
      if (data !== null && data !== undefined) {
        this.recordData.set(data);
        // 初期描画後は、入力データ更新のたびに表示内容を再計算する
        if (this.initialized) {
          this.refreshGostData();
        }
      }
    });
  }

  ngOnInit() {
    this.isLoadingGOSTDataCard.set(false);
    this.initialized = true;

    this.refreshGostData();

    // recordDataUpdated経由の更新もローカルシグナルに反映
    this.recordDataUpdated()?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newRecordData) => {
      this.recordData.set(newRecordData);
      this.refreshGostData();
    });

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentUrl = this.location.path();
      if (!currentUrl.endsWith(this.gostUtils.newGostId)) {
        this.moveBackUrl = currentUrl;
      }
    });
  }

  ngAfterViewInit() {}

  refreshGostData() {
    const data = this.recordData();
    if (data == null) {
      this.isLoadingGOSTDataCard.set(false);
      this.sortSelectedFoods();
      this.cdr.markForCheck();
      return;
    }

    this.asOfDate = data?.data?.asOfDate ?? null;
    this.sortSelectedFoods();
    this.ensureInputBaseline(data);

    try {
      this.gostUtils.evaluateGost(data);
    } catch {
      // 評価途中の不完全データでも画面表示は継続する
    }

    this.updateGrid();
    this.updateOverviewData();

    // スライダーの初期値を設定
    this.sliderValue.FILS = data?.data?.filsGrade || 1;
    this.sliderValue.SC = data?.data?.gostSCData?.level || 1;
    this.sliderValue.GOST = 8 - data?.data?.gostLevel + 1 || 1;
    this.sliderValue.DSS = data?.data?.dss || 1;

    this.isLoadingGOSTDataCard.set(true);
    this.cdr.markForCheck();
  }

  get dateAsDateObject(): Date {
    return new Date(this.asOfDate);
  }

  onNavigateOlder() {
    if (this.canNavigateOlder) {
      this.navigateOlder.emit();
    }
  }

  onNavigateNewer() {
    if (this.canNavigateNewer) {
      this.navigateNewer.emit();
    }
  }

  navigateToNewGostPage() {
    this.router.navigate([
      "/home/",
      this.common.getSelectedSection().sectionId,
      "users",
      this.common.getSelectedUserId(),
      "gost",
      "manual",
    ]);
  }

  moveBackToPrevPage() {
    if (this.moveBackUrl) {
      this.router.navigate([this.moveBackUrl]);
    } else {
      this.router.navigate([
        "/home/",
        this.common.getSelectedSection().sectionId,
        "users",
        this.common.getSelectedUserId(),
        "gost",
        "overview",
      ]);
    }
  }

  getRankClass(rank: number): string {
    return `rank-${rank}`;
  }

  getStars(rank: number): string {
    return `${"★".repeat(5 - rank)}${"☆".repeat(rank - 1)}`;
  }

  convertNewlinesToBr(text: string): string {
    return text ? text.replace(/\n/g, "<br>") : "";
  }

  uploadGost2Data() {
    if (!this.isInputMode() || this.isSavingManual() || !this.hasPendingChanges()) {
      return;
    }

    this.isSavingManual.set(true);
    this.createGost2Service.createGost2(this.recordData()).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.isSavingManual.set(false)),
    ).subscribe((res) => {
      const newGostRecord = (res as any).record;
      this.common.appendUserRecordsList(newGostRecord);

      this.router.navigate([
        "home",
        newGostRecord.meta.location.sectionId,
        "users",
        newGostRecord.meta.user.userId,
        "gost",
      ]);
    });
  }

  onUploadDateChange() {
    if (!this.isInputMode()) {
      return;
    }

    const data = this.recordData();
    if (data) {
      data.data.asOfDate = (
        document.getElementById("upload_date") as HTMLInputElement
      ).value;
      this.asOfDate = data.data.asOfDate;
    }
  }

  hasPendingChanges(): boolean {
    if (!this.isInputMode()) {
      return false;
    }
    const current = this.recordData();
    if (!current || !this.inputBaselineSnapshot) {
      return false;
    }
    return this.buildInputSnapshot(current) !== this.inputBaselineSnapshot;
  }

  canCopyPreviousValues(): boolean {
    return this.getPreviousGostRecordId() !== null;
  }

  confirmClearInputContents() {
    if (!this.isInputMode()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: false,
      data: {
        title: $localize`:@@home.gostDigest.dialog.clearInputsTitle:入力内容をクリア`,
        message: $localize`:@@home.gostDigest.dialog.clearInputsMessage:入力した内容をすべてクリアします。よろしいですか？`,
        confirmButtonText: $localize`:@@home.gostDigest.dialog.clearInputsConfirm:クリアする`,
        variant: "danger",
      },
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => {
      if (confirmed) {
        this.clearInputContents();
      }
    });
  }

  async copyFromPreviousRecord() {
    if (!this.isInputMode() || this.isCopyingPrevious()) {
      return;
    }

    const previousRecordId = this.getPreviousGostRecordId();
    if (!previousRecordId) {
      return;
    }

    this.isCopyingPrevious.set(true);
    try {
      const listRecord = this.common
        .getUserGostsList()
        ?.find((record) => record?.id === previousRecordId);
      const previousRecord = this.hasRequiredGostInputData(listRecord)
        ? listRecord
        : await this.common.getGostRecord(previousRecordId);

      if (!this.hasRequiredGostInputData(previousRecord)) {
        return;
      }

      const current = this.cloneDeep(this.recordData());
      if (!current?.data) {
        return;
      }

      current.data.gostSCData = this.cloneDeep(previousRecord.data.gostSCData);
      current.data.gostGCData = this.cloneDeep(previousRecord.data.gostGCData);
      current.data.gostOCData = this.cloneDeep(previousRecord.data.gostOCData);

      this.recordData.set(current);
      this.pushRecordUpdate(current);
      this.refreshGostData();
    } catch (error) {
      console.error("[ERROR] Failed to copy previous GOST values.", error);
    } finally {
      this.isCopyingPrevious.set(false);
    }
  }

  isInputMode() {
    return this.recordData()?.id == this.gostUtils.newGostId;
  }

  private clearInputContents() {
    if (!this.inputBaselineRecord) {
      return;
    }

    const currentAsOfDate = this.recordData()?.data?.asOfDate ?? null;
    const resetRecord = this.cloneDeep(this.inputBaselineRecord);
    if (currentAsOfDate && resetRecord?.data) {
      resetRecord.data.asOfDate = currentAsOfDate;
    }

    this.recordData.set(resetRecord);
    this.asOfDate = resetRecord?.data?.asOfDate ?? null;
    this.pushRecordUpdate(resetRecord);
    this.refreshGostData();
  }

  private ensureInputBaseline(record: any) {
    if (!this.isInputMode() || !record?.data) {
      return;
    }

    const createdAt = record.data.createdAt ?? null;
    const shouldResetBaseline =
      this.inputBaselineSnapshot === null ||
      this.inputBaselineCreatedAt !== createdAt;

    if (!shouldResetBaseline) {
      return;
    }

    this.inputBaselineSnapshot = this.buildInputSnapshot(record);
    this.inputBaselineRecord = this.cloneDeep(record);
    this.inputBaselineCreatedAt = createdAt;
  }

  private buildInputSnapshot(record: any): string {
    const data = record?.data ?? {};
    return JSON.stringify({
      asOfDate: data?.asOfDate ?? null,
      gostSCData: data?.gostSCData ?? null,
      gostGCData: data?.gostGCData ?? null,
      gostOCData: data?.gostOCData ?? null,
    });
  }

  private hasRequiredGostInputData(record: any): boolean {
    const data = record?.data;
    return !!data?.gostSCData && !!data?.gostGCData && !!data?.gostOCData;
  }

  private getPreviousGostRecordId(): string | null {
    const currentId = this.recordData()?.id;
    const gostRecords = this.common.getUserGostsList();
    if (!Array.isArray(gostRecords)) {
      return null;
    }

    const toTimestamp = (value: unknown): number => {
      if (typeof value !== "string" || value.trim() === "") {
        return Number.NaN;
      }

      const direct = Date.parse(value);
      if (!Number.isNaN(direct)) {
        return direct;
      }

      const normalized = value.replace(/\//g, "-");
      const normalizedTs = Date.parse(normalized);
      return Number.isNaN(normalizedTs) ? Number.NaN : normalizedTs;
    };

    const candidates = gostRecords.filter((record) => {
      const id = record?.id;
      return !!id && id !== this.gostUtils.newGostId && id !== currentId;
    });

    candidates.sort((a, b) => {
      const asOfA = toTimestamp(a?.data?.asOfDate);
      const asOfB = toTimestamp(b?.data?.asOfDate);
      const asOfAValid = Number.isFinite(asOfA);
      const asOfBValid = Number.isFinite(asOfB);

      if (asOfAValid && asOfBValid && asOfA !== asOfB) {
        return asOfB - asOfA;
      }
      if (asOfAValid && !asOfBValid) {
        return -1;
      }
      if (!asOfAValid && asOfBValid) {
        return 1;
      }

      const createdA = toTimestamp(a?.data?.createdAt);
      const createdB = toTimestamp(b?.data?.createdAt);
      const fallbackA = Number.isFinite(createdA) ? createdA : 0;
      const fallbackB = Number.isFinite(createdB) ? createdB : 0;
      return fallbackB - fallbackA;
    });

    return candidates[0]?.id ?? null;
  }

  private pushRecordUpdate(record: any) {
    this.recordDataUpdated()?.next(record);
  }

  private cloneDeep<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  updateGrid() {
    const data = this.recordData();
    const secondarySelectedFoods = data?.data?.SecondarySelectedFoods;
    const resolveFoods = (index: number) => secondarySelectedFoods?.[index]?.selectedFoods;

    this.gridItems = [
      { title: "", contentFirst: "", contentSecond: "" },
      {
        title: "+1",
        ...this.getTopTwoFoods(resolveFoods(7)),
      },
      { title: "", contentFirst: "", contentSecond: "" },
      {
        title: "-1",
        ...this.getTopTwoFoods(resolveFoods(3)),
      },
      {
        title: "0",
        ...this.getTopTwoFoods(resolveFoods(4)),
      },
      {
        title: "+1",
        ...this.getTopTwoFoods(resolveFoods(5)),
      },
      { title: "", contentFirst: "", contentSecond: "" },
      {
        title: "-1",
        ...this.getTopTwoFoods(resolveFoods(1)),
      },
      { title: "", contentFirst: "", contentSecond: "" },
    ];
  }

  getTopTwoFoods(foods: any[]): {
    contentFirst: string;
    contentSecond: string;
  } {
    if (!Array.isArray(foods)) {
      return { contentFirst: "no data", contentSecond: "no data" };
    }

    const rank1 = foods.find((food) => food.rank === 1);
    const rank2 = foods.find((food) => food.rank === 2);
    const noMatch = this.i18n.common.noMatch;

    return {
      contentFirst: `${this.i18n.ranking.first}: ${rank1 ? rank1.name : noMatch}`,
      contentSecond: `${this.i18n.ranking.second}: ${rank2 ? rank2.name : noMatch}`,
    };
  }

  // getGridStyle() を廃止し、CSSクラスベースに変更
  getGridClass(item: { title: string; contentFirst: string }): string {
    if (item.contentFirst === "no data") return "grid-item--none";
    if (item.title === "-1") return "grid-item--negative";
    if (item.title === "+1") return "grid-item--positive";
    if (item.title === "0") return "grid-item--current";
    return "grid-item--default";
  }

  getStarsArray(rank: number): number[] {
    const count = Math.max(0, Math.min(4, 5 - rank));
    return this.createIndexArray(count);
  }

  getEmptyStarsArray(rank: number): number[] {
    const count = Math.max(0, Math.min(4, rank - 1));
    return this.createIndexArray(count);
  }

  getZeroStarsArray(_rank: number): number[] {
    return this.createIndexArray(4);
  }

  private createIndexArray(length: number): number[] {
    return Array.from({ length }, (_, i) => i);
  }

  sortSelectedFoods() {
    const data = this.recordData();
    if (data?.data?.PrimarilySelectedFoods?.selectedFoods) {
      this.sortedSelectedFoods =
        data.data.PrimarilySelectedFoods.selectedFoods
          .slice()
          .sort((a: any, b: any) => {
            return a.rank - b.rank;
          });
    }
  }

  getRankForRecommendation(recommendation: string): number {
    const data = this.recordData();
    const selectedFood =
      data?.data?.PrimarilySelectedFoods?.selectedFoods?.find(
        (food: any) => food.name === recommendation,
      );
    return selectedFood ? selectedFood.rank : 0;
  }

  hasOverviewWstCaution(status: GostOverviewWstRoundStatus): boolean {
    if (!status?.measured) {
      return false;
    }
    return status.result !== this.gostUtils.testResultDisp.good;
  }

  getOverviewRiskClass(item: GostOverviewRiskItem): string {
    return item.severity === "bad"
      ? "gost-result-overview__risk-pill--bad"
      : "gost-result-overview__risk-pill--acceptable";
  }

  private updateOverviewData() {
    const data = this.recordData();
    if (!data?.data) {
      this.overviewWstAttemptRows = [];
      this.overviewRiskItems = [];
      this.hasThicknessData = false;
      return;
    }

    const scData = data.data?.gostSCData;
    this.hasThicknessData = this.hasAnyMeasuredThickness(scData);
    this.overviewWstAttemptRows = this.hasThicknessData
      ? this.buildOverviewWstMixedRows(scData)
      : this.buildOverviewWstAttemptRows(scData);
    this.overviewRiskItems = this.buildOverviewRiskItems(data.data);
  }

  private hasAnyMeasuredThickness(scData: any): boolean {
    const steps = scData?.withThicknessSteps ?? [];
    return steps.some((step: any) =>
      (step?.rounds ?? []).some((round: any) => round?.isMeasured),
    );
  }

  /** 通常水のみ: 水量 | 1回目 | 2回目 */
  private buildOverviewWstAttemptRows(scData: any): GostOverviewWstAttemptRow[] {
    const steps = scData?.withoutThicknessSteps ?? [];
    const rows: GostOverviewWstAttemptRow[] = [];

    for (const step of steps) {
      const rawAmount = Number(step?.amount);
      const amount = Number.isFinite(rawAmount) ? rawAmount : null;
      const rounds = step?.rounds ?? [];

      const firstRound =
        rounds.find((round: any) => Number(round?.round) === 1) ??
        rounds[0] ??
        null;
      const secondRound =
        rounds.find((round: any) => Number(round?.round) === 2) ?? null;

      rows.push({
        amount,
        firstStatus: this.getOverviewRoundStatus(firstRound),
        secondStatus: secondRound
          ? this.getOverviewRoundStatus(secondRound)
          : null,
        hasSecond: !!secondRound,
        thicknessStatus: null,
      });
    }

    return rows;
  }

  /** トロミありの場合: 通常水の行にトロミ列を追加 */
  private buildOverviewWstMixedRows(scData: any): GostOverviewWstAttemptRow[] {
    const withoutSteps = scData?.withoutThicknessSteps ?? [];
    const withSteps = scData?.withThicknessSteps ?? [];

    // まず通常水の行をベースに作成
    const rows = this.buildOverviewWstAttemptRows(scData);

    // トロミのステップを水量でマッピング
    const thicknessMap = new Map<number, any>();
    for (const step of withSteps) {
      const raw = Number(step?.amount);
      if (Number.isFinite(raw)) {
        thicknessMap.set(raw, step);
      }
    }

    // 各行にトロミの結果を追加
    for (const row of rows) {
      if (row.amount == null) continue;
      const wStep = thicknessMap.get(row.amount);
      if (!wStep) continue;
      const wRound = (wStep.rounds ?? [])[0] ?? null;
      row.thicknessStatus = wRound
        ? this.getOverviewRoundStatus(wRound)
        : null;
    }

    return rows;
  }

  private getOverviewRoundStatus(round: any): GostOverviewWstRoundStatus {
    if (!round?.isMeasured) {
      return {
        result: this.gostUtils.testResultDisp.notMeasured,
        measured: false,
        swallowPresent: false,
        coughPresent: false,
        abnormalCount: 0,
      };
    }
    const evaluated = this.gostUtils.evaluateScRound(round) ?? {};
    const abnormalCount = [
      evaluated.swallowDelay,
      evaluated.abnormalSound,
      evaluated.multiSwallows,
      evaluated.throatClear,
      evaluated.otherAbbration,
    ].filter((v) => v === "TP").length;

    return {
      result: evaluated.testResult ?? this.gostUtils.testResultDisp.notMeasured,
      measured: true,
      swallowPresent: evaluated.swallowResult === "TP",
      coughPresent: evaluated.coughResult === "TP",
      abnormalCount,
    };
  }

  private buildOverviewRiskItems(data: any): GostOverviewRiskItem[] {
    const items: GostOverviewRiskItem[] = [];

    const gcMetricDefs: { code: string; label: string; options: string[] }[] = [
      { code: "GC_CONSCIOUSNESS_LEVEL", label: this.i18n.gc.consciousnessLevel.label, options: this.i18n.gc.consciousnessLevel.options as unknown as string[] },
      { code: "GC_TEMPERATURE", label: this.i18n.gc.temperature.label, options: this.i18n.gc.temperature.options as unknown as string[] },
      { code: "GC_PULSE_LEVEL", label: this.i18n.gc.pulseLevel.label, options: this.i18n.gc.pulseLevel.options as unknown as string[] },
      { code: "GC_SPO2", label: this.i18n.gc.spo2.label, options: this.i18n.gc.spo2.options as unknown as string[] },
      { code: "GC_BREATH_LEVEL", label: this.i18n.gc.breathLevel.label, options: this.i18n.gc.breathLevel.options as unknown as string[] },
      { code: "GC_ON_OXYGEN", label: this.i18n.gc.onOxygen.label, options: this.i18n.gc.onOxygen.options as unknown as string[] },
      { code: "GC_SPUTUM", label: this.i18n.gc.sputum.label, options: this.i18n.gc.sputum.options as unknown as string[] },
    ];

    const ocMetricDefs: { code: string; label: string; options: string[] }[] = [
      { code: "OC_TEETH", label: this.i18n.oc.teeth.label, options: this.i18n.oc.teeth.options as unknown as string[] },
      { code: "OC_DENTURE", label: this.i18n.oc.denture.label, options: this.i18n.oc.denture.options as unknown as string[] },
      { code: "OC_ORAL_HYGIENE", label: this.i18n.oc.oralHygiene.label, options: this.i18n.oc.oralHygiene.options as unknown as string[] },
      { code: "OC_ARTICULATION_LEVEL", label: this.i18n.oc.articulationLevel.label, options: this.i18n.oc.articulationLevel.options as unknown as string[] },
      { code: "OC_RIGHT_ARM", label: this.i18n.oc.rightArm.label, options: this.i18n.oc.rightArm.options as unknown as string[] },
      { code: "OC_LEFT_ARM", label: this.i18n.oc.leftArm.label, options: this.i18n.oc.leftArm.options as unknown as string[] },
      { code: "OC_DISABILITY_LEVEL", label: this.i18n.oc.disabilityLevel.label, options: this.i18n.oc.disabilityLevel.options as unknown as string[] },
      { code: "OC_COGNITIVE_LEVEL", label: this.i18n.oc.cognitiveLevel.label, options: this.i18n.oc.cognitiveLevel.options as unknown as string[] },
    ];

    const collectItems = (
      metricsData: any,
      metricDefs: { code: string; label: string; options: string[] }[],
      section: "GC" | "OC",
    ) => {
      const metrics = metricsData?.metrics ?? [];
      for (const def of metricDefs) {
        const metric = metrics.find((m: any) => m?.code === def.code);
        const score = metric?.score;
        if (score == null || score >= 0) {
          continue;
        }
        const optionIndex = score + 3;
        const optionLabel = def.options[optionIndex] ?? "未選択";
        items.push({
          section,
          itemLabel: def.label,
          optionLabel,
          severity: score <= -3 ? "bad" : "acceptable",
        });
      }
    };

    collectItems(data?.gostGCData, gcMetricDefs, "GC");
    collectItems(data?.gostOCData, ocMetricDefs, "OC");

    return items;
  }

  onWindowScroll() {
    const buttonOffset = 100;
    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    if (scrollPosition > buttonOffset) {
      this.isFixed = true;
    } else {
      this.isFixed = false;
    }
  }
}
